import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card } from './../../components/card/card';
import { DynamicChart } from '../analytics/plots/chart';
import Icon from '../../components/icon/icon';
import { Charts } from '../analytics/plots/plots';
import { Table } from './table';
import { useFetch } from "../../components/hooks/useFetch";
import { Loader } from "../../components/loader/loader";
import config from "../../config/config";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.05,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.16, 1, 0.3, 1],
    },
  },
};

export function Dashboard() {
  const { data, loading, error } = useFetch(config.api.url);
  const [dataSet, setDataSet] = useState([]);

  const mean = (dataFiltered, parameter) => {
    if (dataFiltered && dataFiltered.length > 0) {
      return (
        dataFiltered.map((item) => Number(item[parameter])).reduce((sum, value) => sum + value, 0) /
        dataFiltered.length
      ).toFixed(2);
    }
    return "0.00";
  };

  const getFilteredData = (dataSet, startDate, endDate) => {
    const [sy, sm, sd] = startDate.split('-').map(Number);
    const [ey, em, ed] = endDate.split('-').map(Number);
    const start = new Date(sy, sm - 1, sd, 0, 0, 0, 0);
    const end = new Date(ey, em - 1, ed, 23, 59, 59, 999);
    return dataSet.filter((item) => {
      const fecha = new Date(item.date);
      return fecha >= start && fecha <= end;
    });
  };

  const dataTime = [
    {
      title: 'Última Semana',
      subtitle: '03 Ago - 10 Ago',
      valueNox: mean(getFilteredData(dataSet, '2025-08-03', '2025-08-10'), 'nox'),
      valueCO: mean(getFilteredData(dataSet, '2025-08-03', '2025-08-10'), 'co'),
      accent: 'from-blue-500/10 to-indigo-500/5',
      border: 'border-blue-100',
    },
    {
      title: 'Último Mes',
      subtitle: '10 Jul - 10 Ago',
      valueNox: mean(getFilteredData(dataSet, '2025-07-10', '2025-08-10'), 'nox'),
      valueCO: mean(getFilteredData(dataSet, '2025-07-10', '2025-08-10'), 'co'),
      accent: 'from-purple-500/10 to-violet-500/5',
      border: 'border-purple-100',
    },
    {
      title: 'Total Histórico',
      subtitle: 'Todas las muestras',
      valueNox: mean(dataSet, 'nox'),
      valueCO: mean(dataSet, 'co'),
      accent: 'from-amber-500/10 to-orange-500/5',
      border: 'border-amber-100',
    },
  ];

  const coValues = dataSet.map((o) => Number(o.co)).filter((v) => !isNaN(v));
  const noxValues = dataSet.map((o) => Number(o.nox)).filter((v) => !isNaN(v));

  const maxCO = coValues.length ? Math.max(...coValues) : 0;
  const minCO = coValues.length ? Math.min(...coValues) : 0;
  const maxNOX = noxValues.length ? Math.max(...noxValues) : 0;
  const minNOX = noxValues.length ? Math.min(...noxValues) : 0;

  useEffect(() => {
    if (data && Array.isArray(data)) {
      setDataSet(data.filter(({ location }) => location === 'Empresa Grupo Técnico Basse Ltda'));
    }
  }, [data]);

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      <Loader loading={loading} error={error}>
        {/* Page Header */}
        <motion.div
          variants={itemVariants}
          className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-200/60 pb-4"
        >
          <div>
            <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight">
              Dashboard de Telemetría
            </h1>
            <p className="text-xs text-slate-500 font-medium mt-0.5">
              Monitoreo continuo de emisiones en <span className="font-semibold text-slate-700">Empresa Grupo Técnico Basse Ltda</span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-700 border border-slate-200 shadow-xs">
              {dataSet.length} Lecturas Registradas
            </span>
          </div>
        </motion.div>

        {/* Top KPI Metrics Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {dataTime.map(({ title, subtitle, valueNox, valueCO, accent, border }, i) => (
            <motion.div
              key={i}
              variants={itemVariants}
              whileHover={{ y: -4, transition: { duration: 0.2, ease: "easeOut" } }}
              className={`relative overflow-hidden bg-gradient-to-br ${accent} bg-white rounded-2xl border ${border} p-5 shadow-card hover:shadow-card-hover transition-shadow duration-300 flex flex-col justify-between`}
            >
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-600">
                    {title}
                  </span>
                  <span className="w-7 h-7 rounded-lg bg-white/80 border border-slate-100 flex items-center justify-center text-primary shadow-xs">
                    <Icon className="text-xs">chart-line</Icon>
                  </span>
                </div>
                <div className="text-[11px] text-slate-400 font-medium mb-3">{subtitle}</div>
              </div>

              <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-100">
                <div className="bg-white/70 rounded-xl p-2 border border-slate-100 text-center">
                  <span className="text-[10px] font-bold text-slate-400 block uppercase">NOx</span>
                  <span className="text-base font-extrabold text-primary">{valueNox}</span>
                  <span className="text-[10px] text-slate-400 block">ppm</span>
                </div>
                <div className="bg-white/70 rounded-xl p-2 border border-slate-100 text-center">
                  <span className="text-[10px] font-bold text-slate-400 block uppercase">CO</span>
                  <span className="text-base font-extrabold text-indigo-600">{valueCO}</span>
                  <span className="text-[10px] text-slate-400 block">ppm</span>
                </div>
              </div>
            </motion.div>
          ))}

          {/* Bar Chart Summary Card */}
          <motion.div
            variants={itemVariants}
            whileHover={{ y: -4, transition: { duration: 0.2 } }}
            className="col-span-1 md:col-span-3 lg:col-span-2"
          >
            <Card className="h-full p-4 flex flex-col justify-between">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Distribución por Horarios
                </h3>
                <span className="text-[11px] text-slate-400 font-medium">Primer / Segundo Piso</span>
              </div>
              <div className="h-[180px] w-full">
                {Charts(data)
                  .map((plot) => ({
                    ...plot,
                    data: {
                      ...plot.data,
                      datasets: plot.data.datasets.map((ds) => ({
                        ...ds,
                        backgroundColor: ds.label === 'CO' ? '#7C5DFA' : '#38BDF8',
                        borderRadius: 6,
                      })),
                    },
                  }))
                  .map((plot, i) => (
                    <div key={i} className="h-full">
                      <DynamicChart height={180} {...plot} />
                    </div>
                  ))[1]}
              </div>
            </Card>
          </motion.div>
        </div>

        {/* Middle Section: Table & Status Card */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <motion.div variants={itemVariants} className="lg:col-span-2">
            <Table data={data} />
          </motion.div>

          {/* Air Quality & Status Gauge Card */}
          <motion.div
            variants={itemVariants}
            whileHover={{ y: -4, transition: { duration: 0.2 } }}
          >
            <Card className="p-5 flex flex-col justify-between h-full">
              <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Índice de Calidad del Aire
                </span>
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
                </span>
              </div>

              <div className="py-4 flex items-center justify-center gap-4 bg-gradient-to-br from-emerald-500/10 to-teal-500/5 rounded-2xl border border-emerald-100 my-2 shadow-xs">
                <div className="w-14 h-14 rounded-2xl bg-emerald-500 text-white flex items-center justify-center text-3xl shadow-md shadow-emerald-500/20">
                  <Icon type="regular">face-smile</Icon>
                </div>
                <div>
                  <span className="text-xs font-semibold text-emerald-600 uppercase tracking-wider">Estado Actual</span>
                  <h2 className="text-2xl font-black text-emerald-700">Bueno / Seguro</h2>
                  <p className="text-[11px] text-slate-500">Niveles dentro del umbral normativo</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 pt-2">
                <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                  <span className="text-[11px] font-bold text-slate-500 block mb-1">CO (Rango)</span>
                  <div className="flex items-baseline justify-between">
                    <span className="text-xs text-slate-400">Min: <strong className="text-slate-700">{minCO}</strong></span>
                    <span className="text-xs text-slate-400">Max: <strong className="text-primary font-bold">{maxCO}</strong></span>
                  </div>
                </div>
                <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                  <span className="text-[11px] font-bold text-slate-500 block mb-1">NOx (Rango)</span>
                  <div className="flex items-baseline justify-between">
                    <span className="text-xs text-slate-400">Min: <strong className="text-slate-700">{minNOX}</strong></span>
                    <span className="text-xs text-slate-400">Max: <strong className="text-primary font-bold">{maxNOX}</strong></span>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>

        {/* Bottom Section: Main Timeline Chart */}
        <motion.div
          variants={itemVariants}
          whileHover={{ y: -2, transition: { duration: 0.2 } }}
        >
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-100">
              <div>
                <h2 className="text-base font-bold text-slate-800">
                  Evolución Temporal de Emisiones (CO vs NOx)
                </h2>
                <p className="text-xs text-slate-400">
                  Seguimiento cronológico de concentraciones a lo largo del periodo de muestreo
                </p>
              </div>
              <div className="flex items-center gap-4 text-xs font-semibold">
                <div className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-full bg-[#7C5DFA]"></span>
                  <span className="text-slate-600">CO (ppm)</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-full bg-[#38BDF8]"></span>
                  <span className="text-slate-600">NOx (ppm)</span>
                </div>
              </div>
            </div>
            <div className="h-[360px] w-full">
              {Charts(data)
                .map((plot) => ({
                  ...plot,
                  data: {
                    ...plot.data,
                    datasets: plot.data.datasets.map((ds, idx) => ({
                      ...ds,
                      borderColor: idx === 0 ? '#7C5DFA' : '#0284C7',
                      backgroundColor: idx === 0 ? 'rgba(124, 93, 250, 0.1)' : 'rgba(2, 132, 199, 0.1)',
                      borderWidth: 2.5,
                      pointBackgroundColor: idx === 0 ? '#7C5DFA' : '#0284C7',
                      pointBorderColor: '#ffffff',
                      pointBorderWidth: 2,
                      pointRadius: 4,
                      pointHoverRadius: 6,
                    })),
                  },
                }))
                .map((plot, i) => (
                  <div key={i} className="h-full">
                    <DynamicChart height={340} {...plot} />
                  </div>
                ))[0]}
            </div>
          </Card>
        </motion.div>
      </Loader>
    </motion.div>
  );
}


